/**
 * Canvas Adapter
 *
 * Converts between Rool app data formats and CanvasEditor formats.
 * With unified top-left position convention, conversions are minimal.
 *
 * Node/edge rendering conversion is handled internally by CanvasEditor
 * (defaultNodeToUINode / defaultEdgeToUIEdge). This file only handles
 * the app-specific UINode <-> AppNode / UIEdge <-> AppEdge transforms.
 */

import type { UINode, UIEdge } from "@rool/renderer/types";
import type { EditorNode, EditorEdge } from "@rool/editor";

// =============================================================================
// App Node/Edge Types (EditorNode with app-specific fields)
// =============================================================================

export interface AppNode extends EditorNode {
  label: string;
}

export interface AppEdge extends EditorEdge {
  // Edges use sourceNodeId and targetNodeId from EditorEdge
}

// =============================================================================
// UINode <-> AppNode Conversion
// =============================================================================

/**
 * Convert UINode to AppNode. Both use top-left positions.
 */
export function uiNodeToAppNode(node: UINode): AppNode {
  // Extract label from node data
  const nodeData = node.data as Record<string, unknown> | undefined;
  const label = (nodeData?.label || nodeData?.title || node.id) as string;

  return {
    ...node,
    position: { x: node.position.x, y: node.position.y },
    label,
  };
}

// =============================================================================
// UIEdge <-> AppEdge Conversion
// =============================================================================

/**
 * Convert UIEdge to AppEdge.
 */
export function uiEdgeToAppEdge(edge: UIEdge): AppEdge {
  return {
    id: edge.id,
    type: edge.type ?? 'default',
    source: edge.source,
    target: edge.target,
    sourceNodeId: edge.source,
    targetNodeId: edge.target,
    data: edge.data ?? {},
  };
}
